import { flexRender } from "@tanstack/react-table";
import "./TableHead.scss";
import {
  ArrowDownUp,
  DownArrow,
  DragHandleIcon,
  UpArrow,
  VerticalThreeDots,
} from "../assets/svg";
import DropdownMenu from "./DropdownMenu";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import {
  getColumnJustifyContent,
  getColumnPinningStyles,
} from "../utils/styleUtils";
import { DebouncedInput } from "./Topbar";
import { CSS } from "@dnd-kit/utilities";

function TableHead({ table, tableOptions, columnOrder, featureOptions }) {
  const { enableColumnReording } = featureOptions;

  return (
    <thead className="ts__head">
      {table?.getHeaderGroups()?.map((headerGroup) => (
        <tr className="ts__head__tr" key={headerGroup.id}>
          {enableColumnReording ? (
            <SortableContext
              items={columnOrder}
              strategy={horizontalListSortingStrategy}
            >
              {headerGroup?.headers?.map((header) => (
                <DraggableTableHeader
                  key={header.id}
                  header={header}
                  featureOptions={featureOptions}
                  tableOptions={tableOptions}
                />
              ))}
            </SortableContext>
          ) : (
            headerGroup?.headers?.map((header) => {
              return (
                <th
                  className={`ts__head__th ${
                    header.isPlaceholder ? "" : "ts__border"
                  }`}
                  key={header?.id}
                  style={{
                    ...getColumnPinningStyles(header.column),
                    width: `${header.column.getSize()}px `,
                    minWidth: `${header.column.columnDef.minSize}px`,
                    maxWidth: `${header.column.columnDef.maxSize}px`,
                  }}
                  colSpan={header.colSpan}
                  align={header.column.columnDef.meta?.align || "left"}
                >
                  <TableHeadContent
                    featureOptions={featureOptions}
                    header={header}
                    tableOptions={tableOptions}
                  />
                </th>
              );
            })
          )}
        </tr>
      ))}
    </thead>
  );
}

export default TableHead;

function DraggableTableHeader({ header, featureOptions, tableOptions }) {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });

  const style = {
    opacity: isDragging ? 0.6 : 1,
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    minWidth: `${header.column.columnDef.minSize ?? 180}px`,
    maxWidth: `${header.column.columnDef.maxSize}px`,
    zIndex: isDragging ? 1 : 0,
    padding: `${tableOptions.currentPadding}`,
  };

  return (
    <th
      ref={setNodeRef}
      className={`ts__head__th ${header.isPlaceholder ? "" : "ts__border"}`}
      key={header.id}
      style={{
        ...getColumnPinningStyles(header.column),
        ...style,
      }}
      colSpan={header.colSpan}
      align={header.column.columnDef.meta?.align || "left"}
    >
      <TableHeadContent
        header={header}
        featureOptions={featureOptions}
        tableOptions={tableOptions}
      />
    </th>
  );
}

export function Filter({ column }) {
  const columnFilterValue = column.getFilterValue();
  return (
    <div>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`search by ${column.columnDef.header}`}
      />
    </div>
  );
}

function TableHeadContent({ header, featureOptions, tableOptions }) {
  const { enableSorting, enableVerticalDots, enableColumnReording } =
    featureOptions;
  const { isDropdownOpen, setIsDropdownOpen, openFilter } = tableOptions;
  const { attributes, listeners } = useSortable({
    id: header.column.id,
  });
  const handleDropdownClickForColumn = (id) => {
    if (isDropdownOpen === id) {
      setIsDropdownOpen("");
    } else {
      setIsDropdownOpen(id);
    }
  };

  const alignment = header.column.columnDef.meta?.align;
  const justifyContentStyle = getColumnJustifyContent(alignment);

  return (
    <>
      {isDropdownOpen === header.id && enableVerticalDots && (
        <DropdownMenu top={50} right={16}>
          {!header.isPlaceholder &&
            header.column.getCanSort() &&
            enableSorting && (
              <div className="ts__head__dropdown__group">
                <button
                  disabled={header.column.getIsSorted() === false}
                  onClick={() => {
                    header.column.clearSorting();
                    handleDropdownClickForColumn(header.id);
                  }}
                >
                  Clear Sort
                </button>
                <button
                  disabled={header.column.getIsSorted() === "asc"}
                  onClick={() => {
                    header.column.toggleSorting();
                    handleDropdownClickForColumn(header.id);
                  }}
                >
                  Sort ascending
                </button>
                <button
                  disabled={header.column.getIsSorted() === "desc"}
                  onClick={() => {
                    header.column.toggleSorting();
                    handleDropdownClickForColumn(header.id);
                  }}
                >
                  Sort descending
                </button>
              </div>
            )}
          {header.column.getCanSort() && <div className="divider"></div>}
          {!header.isPlaceholder && header.column.getCanPin() && (
            <div className="ts__head__dropdown__group">
              <button
                disabled={header.column.getIsPinned() === "left"}
                onClick={() => {
                  header.column.pin("left");
                  handleDropdownClickForColumn(header.id);
                }}
              >
                Pin to left
              </button>
              <button
                disabled={header.column.getIsPinned() === "right"}
                onClick={() => {
                  header.column.pin("right");
                  handleDropdownClickForColumn(header.id);
                }}
              >
                Pin to Right
              </button>
              <button
                disabled={header.column.getIsPinned() === false}
                onClick={() => {
                  header.column.pin(false);
                  handleDropdownClickForColumn(header.id);
                }}
              >
                Unpin
              </button>
            </div>
          )}
          {header.column.getCanPin() && <div className="divider"></div>}
          {!header.isPlaceholder && header.column.getCanGroup() && (
            <div className="ts__head__dropdown__group">
              <button
                onClick={() => {
                  header.column.toggleGrouping();
                  handleDropdownClickForColumn(header.id);
                }}
              >
                {header.column.getIsGrouped()
                  ? `Ungroup by ${header.column.columnDef.header}`
                  : `Group by ${header.column.columnDef.header}`}
              </button>
            </div>
          )}
        </DropdownMenu>
      )}
      {header.isPlaceholder ? null : (
        <div>
          <div
            className="ts__head__th__container"
            style={{ justifyContent: justifyContentStyle }}
          >
            <span
              onClick={
                enableSorting
                  ? header.column.getToggleSortingHandler()
                  : () => {}
              }
            >
              <>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}{" "}
                {{ asc: <UpArrow />, desc: <DownArrow /> }[
                  header.column.getIsSorted()
                ] ?? null}
              </>
            </span>
            {enableColumnReording && (
              <div {...attributes} {...listeners} className="ts__dnd__button">
                <DragHandleIcon />
              </div>
            )}
            {header.subHeaders.length === 0
              ? enableVerticalDots && (
                  <div onClick={() => handleDropdownClickForColumn(header.id)}>
                    {" "}
                    <VerticalThreeDots />
                  </div>
                )
              : null}
          </div>
          {openFilter && header.subHeaders.length === 0 ? (
            header.column.getCanFilter() ? (
              <div className="ts__head__th__filter">
                <Filter column={header.column} />
              </div>
            ) : null
          ) : null}
        </div>
      )}
      {header.column.getCanResize() ? (
        <div
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`column__resize ${
            header.column.getIsResizing() ? "is__resizing" : ""
          }`}
        />
      ) : null}
    </>
  );
}
